import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from "react-redux";
import { callNotify } from '../../../../../../../..';
import {
    callEventOperation,
    getOperation,
    setOperation,
    useOperation
} from "../../../../../../../../contexts/OperationContext";
import { callEventPlan, getPlan, setPlan } from "../../../../../../../../contexts/PlanOperationContext/Plan";
import { openPopup2 } from '../../../../../../../../contexts/PopupContext';
import { getStore } from "../../../../../../../../util/store/store";
import { OP_STATUS, OPERATION_TYPE } from "../../../../../../../../util/varibles/constants";
import {
    checkLimit,
    getWidthByClassName,
    removeClassName
} from "../../../../../../../../util/varibles/global";
import { IOperation, IPropsChildren } from "../../../../../../../../util/varibles/interface";
import PopupMerge from "../../../../../../Popup/OpMerge";
import { LAYOUT_CALENDER, selectPlan } from "../../../../../../constants";
import { planOpActions } from "../../../../../../reducer";
import { updateCrewTasksTime, updateTime } from '../../../../../../util/function_operation/global';
import stylesVessel from "../../../../Vessels/style.module.scss";
import { EL_RULE } from "../../../../constants";
import { handleScroll } from '../../../actions';
import { IPropsOp } from "../../index";
import styles from "../../style.module.scss";
import stylesTime from '../TimeLine/style.module.scss';
import { checkStatus, getNewTime } from "../constants";

export interface IPropsWrapperOp extends IPropsChildren, IPropsOp {
    status: OP_STATUS;
    data: IOperation;
    operationType: OPERATION_TYPE;
    isOwn: boolean;

    event?: {
        onMouseOver?(e: React.MouseEvent): void;
        onMouseLeave?(e: React.MouseEvent): void;
    };

    edit?(): void;
}

export default function useOperationInteractions(props: IPropsWrapperOp) {
    const [realStatus] = useOperation(state => state.realStatus);
    const { operationType, data, event, edit, readOnly } = props;
    const { operation, action_type, status: validationStatus } = props.data;
    const { id, status, parallel } = operation;

    const { setTarget } = callEventPlan();
    const { getActivityLog, resetState, openInfo } = callEventOperation();

    const setOp = setOperation();
    const getOp = getOperation();

    const setContainerOfOp = setPlan();
    const getContainerOfOp = getPlan();

    const rootRef = useRef<HTMLDivElement>(null);
    const planRef = useRef<HTMLElement>();
    const autoScrollFrameRef = useRef<number>();
    const autoScrollArgsRef = useRef<Parameters<typeof handleScroll>[0]>();
    const animationFrameRef = useRef<number>();
    const latestMouseMoveRef = useRef<MouseEvent>();
    const interactionHandlersRef = useRef<{
        mouseMove(e: MouseEvent): void;
        mouseUp(e: MouseEvent): void;
        clickOutside(e: MouseEvent): void;
    }>();

    const onDocumentMouseMove = useCallback((e: MouseEvent) => {
        interactionHandlersRef.current?.mouseMove(e);
    }, []);
    const onDocumentMouseUp = useCallback((e: MouseEvent) => {
        interactionHandlersRef.current?.mouseUp(e);
    }, []);
    const onPlanMouseDown = useCallback((e: MouseEvent) => {
        interactionHandlersRef.current?.clickOutside(e);
    }, []);

    const dispatch = useDispatch();

    const stopAutoScroll = () => {
        if (autoScrollFrameRef.current !== undefined) {
            window.cancelAnimationFrame(autoScrollFrameRef.current);
            autoScrollFrameRef.current = undefined;
        }
        autoScrollArgsRef.current = undefined;
    };

    const runAutoScroll = () => {
        const args = autoScrollArgsRef.current;

        if (!args || !handleScroll(args)) {
            autoScrollFrameRef.current = undefined;
            return;
        }

        autoScrollFrameRef.current = window.requestAnimationFrame(runAutoScroll);
    };

    const startAutoScroll = (args: Parameters<typeof handleScroll>[0]) => {
        autoScrollArgsRef.current = args;

        if (autoScrollFrameRef.current === undefined) {
            autoScrollFrameRef.current = window.requestAnimationFrame(runAutoScroll);
        }
    };

    const cancelMouseMoveFrame = () => {
        if (animationFrameRef.current !== undefined) {
            window.cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = undefined;
        }
        latestMouseMoveRef.current = undefined;
    };

    const getOperationElement = (target: EventTarget | null) =>
        target instanceof Element
            ? target.closest<HTMLElement>(`.${styles.operation}`)
            : null;

    const isInsideCalendar = (clientX: number, clientY: number) => {
        const calendar = getContainerOfOp().calendarRef?.();

        if (!calendar) {
            return false;
        }

        const { left, right, top, bottom } = calendar.getBoundingClientRect();
        return clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
    };

    const getVisibleMaxLeft = (fallback: number) => {
        const calendar = getContainerOfOp().calendarRef?.();
        const layout = document.getElementById(LAYOUT_CALENDER);
        const operation = rootRef.current;

        if (!calendar || !layout || !operation) {
            return fallback;
        }

        const calendarRect = calendar.getBoundingClientRect();
        const layoutRect = layout.getBoundingClientRect();
        return calendarRect.right - layoutRect.left - operation.offsetWidth - 20;
    };

    useEffect(() => {
        planRef.current = getContainerOfOp().calendarRef?.();

        const opFocusId = selectPlan.opFocusId(getStore());

        if (opFocusId === id && planRef.current) {
            planRef.current.addEventListener("mousedown", onPlanMouseDown);
        }

        setOp({
            isTimeline: id === opFocusId,
            rootRef: () => rootRef.current as HTMLDivElement
        });

        return () => {
            stopAutoScroll();
            cancelMouseMoveFrame();

            document.removeEventListener('mousemove', onDocumentMouseMove);
            document.removeEventListener('mouseup', onDocumentMouseUp);

            if (planRef.current) {
                planRef.current.removeEventListener("mousedown", onPlanMouseDown);
            }

            setContainerOfOp({ isDrag: '' });
        };
        // Operation drag/outside-click listeners are registered imperatively and cleaned on unmount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, setOp, setContainerOfOp]);

    const handleMouseDown = (e: any) => {
        const { isOwn = false, isDrag } = getOp();
        const { permission } = getContainerOfOp();

        if (!readOnly && edit && setTarget && permission.OP_MENU) {
            setTarget({ type: EL_RULE.OPERATION, data, isOwn, edit }, e);
        }

        e.preventDefault();
        e.stopPropagation();

        if (readOnly) {
            if (e.button === 0)
                openInfo();
            return;
        }

        if (e.button === 2 || !rootRef.current) {
            return;
        }

        if (planRef.current) {
            planRef.current.addEventListener("mousedown", onPlanMouseDown);
        }

        setOp({ vesselWidth: getWidthByClassName(stylesVessel.vessel) });

        const { calendarRef } = getContainerOfOp();

        if (calendarRef && calendarRef()) {
            calendarRef().dataset.modeDrag = `${operationType}`;
        }

        setContainerOfOp({ isDrag: 'true' });

        document.addEventListener('mouseup', onDocumentMouseUp);

        const isStatusValid = checkStatus(status);
        const state: any = { modeDrag: 'down', isTimeline: true };
        const { getVessels, loadingKeys, currentKey } = getOp();

        if (
            isStatusValid &&
            isOwn &&
            isDrag &&
            !loadingKeys[currentKey] &&
            permission.UPDATE_OP
        ) {
            document.addEventListener('mousemove', onDocumentMouseMove);

            const { offsetWidth } = rootRef.current;
            const { left, top } = rootRef.current.getBoundingClientRect();
            const layoutRect = document.getElementById(LAYOUT_CALENDER)?.getBoundingClientRect();

            state.targetX = e.clientX - left;
            state.targetY = e.clientY - (layoutRect?.top ?? top);
            const calendar = calendarRef?.();
            state.maxWidth = (calendar?.scrollWidth || 0) - offsetWidth;
            state.maxScrollLeft = Math.max(
                0,
                (calendar?.scrollWidth || 0) - (calendar?.clientWidth || 0)
            );
            state.scrollHeight = calendar?.scrollHeight;

            const vessels = getVessels(getStore());
            state.vessels = vessels.filter(({ isShow }) => isShow);
        }

        document.body.setAttribute('data-is-prevent-select', 'true');

        setOp(state);
    };

    const processMouseMove = (e: MouseEvent) => {
        if (!rootRef.current) {
            return;
        }

        const { target } = e;

        if (autoScrollArgsRef.current) {
            autoScrollArgsRef.current.clientY = e.clientY;
        }

        const operationElement = getOperationElement(target);
        const positionId = operationElement?.id || (target as HTMLElement | null)?.id || null;
        const isOperation = operationElement !== null;

        if (isOperation && positionId !== id) {
            stopAutoScroll();
            rootRef.current.style.display = 'none';

            const { isTimeline, modeDrag } = getOp();

            if (isTimeline || modeDrag !== 'move') {
                setOp({ isTimeline: false, modeDrag: 'move' });
            }

            return;
        }

        const state = { isTimeline: true, modeDrag: 'move' };

        rootRef.current.style.display = 'block';

        if (positionId !== LAYOUT_CALENDER && !isInsideCalendar(e.clientX, e.clientY)) {
            stopAutoScroll();
            resetState({ isTimeline: true, modeDrag: 'move' });
            return;
        }

        const { targetX = 0, maxWidth = 0, maxScrollLeft = 0, activityLogs } = getOp();

        const {
            newStartTime,
            currentKey,
            rootKey,
            vessel,
            isAction,
            x
        } = getNewTime({ get: getOp, activityLogs, data, e });

        if (isAction && data.operation.operation_type !== OPERATION_TYPE.SERVICE) {
            getActivityLog({
                time: newStartTime,
                currentKey,
                vessel
            });
        }

        document.body.style.cursor = 'grabbing';

        const operationWidth = rootRef.current.offsetWidth;
        const visibleMaxLeft = getVisibleMaxLeft(maxWidth);
        const targetLeft = checkLimit(10, Math.min(maxWidth, visibleMaxLeft), x - targetX);
        const layoutRect = document.getElementById(LAYOUT_CALENDER)?.getBoundingClientRect();
        const operationClientLeft = (layoutRect?.left ?? 0) + targetLeft;
        const operationClientRight = operationClientLeft + operationWidth;
        const isSameStartTime = newStartTime === activityLogs[rootKey][0].est_start_time;
        const nextActivityLogs = isSameStartTime
            ? activityLogs
            : {
                ...activityLogs,
                [rootKey]: updateTime(activityLogs[rootKey], 0, newStartTime)
            };

        setOp({
            ...state,
            left: targetLeft,
            rootKey,
            currentKey,
            activityLogs: nextActivityLogs
        });

        startAutoScroll({
            operationClientLeft,
            operationClientRight,
            clientY: e.clientY,
            key: rootKey,
            maxScrollLeft,
            activityLogs: nextActivityLogs,
            action: (updatedActivityLogs) => {
                if (autoScrollArgsRef.current) {
                    autoScrollArgsRef.current.activityLogs = updatedActivityLogs;
                }
                setOp({ activityLogs: updatedActivityLogs });
            },
            moveHorizontal: (distance) => {
                setOp(previous => ({
                    left: checkLimit(
                        10,
                        Math.min(maxWidth, getVisibleMaxLeft(maxWidth)),
                        (previous.left ?? rootRef.current?.offsetLeft ?? targetLeft) + distance
                    )
                }));
            }
        });
    };

    const handleMouseMove = (e: MouseEvent) => {
        latestMouseMoveRef.current = e;

        if (animationFrameRef.current !== undefined) {
            return;
        }

        animationFrameRef.current = window.requestAnimationFrame(() => {
            animationFrameRef.current = undefined;
            const latestEvent = latestMouseMoveRef.current;
            latestMouseMoveRef.current = undefined;

            if (latestEvent) {
                processMouseMove(latestEvent);
            }
        });
    };

    const handleMouseUp = (e: any) => {
        cancelMouseMoveFrame();

        if (!rootRef.current) {
            return;
        }

        const { calendarRef } = getContainerOfOp();

        if (calendarRef && calendarRef()) {
            calendarRef().dataset.modeDrag = '';
        }

        setContainerOfOp({ isDrag: '' });

        document.body.style.cursor = 'default';
        document.body.setAttribute('data-is-prevent-select', 'false');

        document.removeEventListener('mousemove', onDocumentMouseMove);
        document.removeEventListener('mouseup', onDocumentMouseUp);

        const { modeDrag } = getOp();

        if (modeDrag !== 'move') {
            rootRef.current.style.display = 'block';
            openInfo();
            return;
        }

        stopAutoScroll();

        setOp({ modeDrag: '' });

        const { clientX, clientY, target } = e;
        const operationElement = getOperationElement(target);
        const positionId = operationElement?.id || (target as HTMLElement | null)?.id || null;
        const isOperation = operationElement !== null;

        if (isOperation) {
            const target = operationElement;
            const id = target?.id || '';

            if (target) {
                removeClassName(target, styles['merge-hover']);
            }

            const cancel = () => {
                if (rootRef.current) {
                    rootRef.current.style.display = 'block';
                }

                resetState({ isTimeline: true });
            };

            if (id === operation.id) {
                cancel();
                return;
            }

            openPopup2(
                <PopupMerge idTarget={id} opDrag={data} />,
                {
                    modalProps: {
                        title: "Merge Operation",
                        width: 860
                    },
                    onClose: cancel
                }
            );

            return;
        }

        if (positionId !== LAYOUT_CALENDER && !isInsideCalendar(clientX, clientY)) {
            resetState({ isTimeline: true });
            return;
        }

        const {
            newStartTime,
            currentKey,
            vessel,
            vesselIndex,
            error,
            isCheck
        } = getNewTime({ get: getOp, data, e });

        if (error.length > 0) {
            if (error !== '-') {
                callNotify?.error({
                    title: 'Error',
                    description: error
                });
            }

            resetState({});
            return;
        }

        let available_time_id: any = '-';
        let contract_id: any = '-';

        if (!vessel.isOwn) {
            available_time_id = operation.available_time_id;
            contract_id = operation.contract_id;
        }

        rootRef.current.style.display = 'none';

        const location = document.elementFromPoint(clientX, clientY) as HTMLElement;

        if (location) {
            const { rule, availableTimeId, contractId } = location.dataset;

            if (rule === EL_RULE.AVAILABLE_TIME) {
                available_time_id = availableTimeId;
            } else if (rule === EL_RULE.CONTRACT) {
                contract_id = contractId;
            }
        }

        rootRef.current.style.display = 'block';

        const valueNew = {
            operation: {
                ...operation,
                ...(operation.operation_type === OPERATION_TYPE.SERVICE ? {
                    crew_tasks: updateCrewTasksTime(
                        operation.crew_tasks,
                        newStartTime - data.activity_log[0].est_start_time
                    )
                } : {}),
                contract_id,
                available_time_id,
                vessel_id: vessel.id
            },
            vessel
        };

        // Drop pixel-based drag positioning so SizeOperation aligns to the target vessel column.
        setOp({ left: undefined, vesselIndex });

        const updateOpEffect = (newOperation: IOperation) => {
            const { onDragOp } = getContainerOfOp();
            onDragOp(newOperation);
        };

        if (isCheck && operation.operation_type !== OPERATION_TYPE.SERVICE) {
            getActivityLog({
                time: newStartTime,
                vessel,
                success: (ops) => {
                    const activityLogNew = ops[id].activity_log;

                    const op = {
                        ...valueNew,
                        operation: ops[id].operation,
                        activity_log: activityLogNew
                    };

                    op.operation.duration =
                        activityLogNew[activityLogNew.length - 1].est_start_time -
                        activityLogNew[0].est_start_time;

                    updateOpEffect(op);
                }
            });

            return;
        }

        const { activityLogs } = getOp();
        const activity_log = updateTime(
            activityLogs[currentKey] || data.activity_log,
            0,
            newStartTime
        );
        const op = { ...valueNew, activity_log, vessel };

        updateOpEffect(op);
    };

    const handleClickOutside = (e: any) => {
        if (e.target.closest(`.${stylesTime.timeline}`)) {
            return;
        }

        if (
            rootRef.current &&
            !rootRef.current.contains(e.target) &&
            !e.target.closest('.ant-dropdown')
        ) {
            setOp({ isTimeline: false });

            const opFocusId = selectPlan.opFocusId(getStore());

            if (opFocusId === id) {
                dispatch(planOpActions.setFocusId(''));
            }

            planRef.current?.removeEventListener("mousedown", onPlanMouseDown);
        }
    };

    interactionHandlersRef.current = {
        mouseMove: handleMouseMove,
        mouseUp: handleMouseUp,
        clickOutside: handleClickOutside
    };

    const common = {
        ...(event || {}),
        id,
        ref: rootRef,
        'data-status': status,
        'data-is-pause': realStatus === OP_STATUS.PAUSE,
        'data-operation-id': id,
        'data-editing': !!action_type,
        'data-operation-status': validationStatus,
        'data-parallel': parallel,
        onMouseDown: handleMouseDown
    };

    return common;
}
